/* =============================================================================
 * local.js — Local プリセットの共有ランタイム
 * -----------------------------------------------------------------------------
 * 出自: 実装はすべて自作。機構の型（rAF マーキー・クラス切替 reveal・
 *       scroll-snap スライダー）は Local テーマのレンダリング挙動からの実測。
 *       実測根拠 → ../../design-system/extracted/local_アニメーション実測.md
 *
 * 読み込み条件: settings.design_language == 'local' のときだけ theme.liquid が読む。
 * 方針:
 *   - WAAPI / ライブラリ不使用（参照テーマと同じくバニラ）。
 *   - すべての動きは prefers-reduced-motion で停止（参照テーマ非対応＝優位点）。
 *   - カスタム要素は lc- プレフィクス。ここには複数セクションで使う共有エンジンのみ置く。
 *     （セクション固有の軽い挙動は各セクションの {% javascript %} に書く）
 *   - Progressive Enhancement: この JS が落ちても内容は全て読める。初期非表示を
 *     作らない／スライダーは素の横スクロールとして成立する。
 * 構成:
 *   §1 lc-slider   — scroll-snap + scrollTo(smooth)。ドット/矢印/インデックスナビ
 *   §2 lc-marquee  — rAF 直書き 200px/s（≤768 ÷1.5）・IO 停止・複製 ceil(幅/W)+1
 *   §3 lc-hotspots — クラス切替開閉・z-index 単調増加・Escape/外側クリック
 *   §4 lc-drawer   — サイドバー開閉（inert・フォーカス移動・overlay 連動）
 *   §5 LcStickyHeader — -100px 閾値・5px デッドゾーン・上スクロールで .show
 * ========================================================================== */
(function () {
  'use strict';
  if (document.documentElement.dataset.design !== 'local') return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** 冪等定義。同じファイルが二重に読まれても例外にしない。 */
  function define(name, ctor) {
    if (!window.customElements || customElements.get(name)) return;
    customElements.define(name, ctor);
  }

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    ' select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]),' +
    ' [contenteditable="true"]';

  /* ===========================================================================
   * §1. <lc-slider>
   * ---------------------------------------------------------------------------
   * 送り自体はブラウザに委譲する（scroll-snap + scrollTo({behavior:'smooth'})）。
   * 参照テーマのデスクトップ用ドラッグ（1px 動いたら1ページ送り）は誤爆源なので
   * 実装しない — タッチは慣性スクロール、マウスはホイール/矢印/ドットで足りる。
   *
   * 属性:
   *   data-nav="dots|index|none"  ナビの型（既定 dots。≤480 は CSS が index に入替）
   *   data-autoplay="6000"        ms。既定 0 = 無効（ホームでは 0 のまま使う）
   * イベント: change（bubbles・detail {index, total}）
   * ======================================================================== */
  class LcSlider extends HTMLElement {
    connectedCallback() {
      if (this.dataset.lcReady === 'true') return;
      this.track = this.querySelector('.lc-slider__track');
      if (!this.track) return;
      /* overflow スクロール領域のキーボード到達（axe scrollable-region-focusable）。
         tabindex のみ付与する — role を書くと <ul> トラックのリスト意味論を壊し
         （listitem違反）、同一ラベルの region 乱立は landmark-unique 違反になる。 */
      this.track.tabIndex = 0;
      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('.lc-slide'));
      if (this.slides.length < 1) return;
      this.dataset.lcReady = 'true';

      this.index = 0;
      /* プログラム送りの直後は IO の index 更新を止める（実測: 参照も 500ms 封じる）。
         止めないとスクロール途中の中間スライドで index が振動する。 */
      this.lockUntil = 0;

      this.buildNav();
      this.observe();
      this.onKeydown = this.handleKeydown.bind(this);
      this.addEventListener('keydown', this.onKeydown);

      this.autoplayMs = parseInt(this.dataset.autoplay || '0', 10) || 0;
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.io) this.io.disconnect();
      this.removeEventListener('keydown', this.onKeydown);
    }

    /* --- ナビ生成（DOM に無くても JS だけで足りる＝セクション側は器だけ用意） --- */
    buildNav() {
      var mode = this.dataset.nav || 'dots';
      if (mode === 'none' || this.slides.length < 2) return;

      var nav = document.createElement('div');
      nav.className = 'lc-slider-nav lc-slider-nav--' + mode;

      this.prevBtn = this.makeArrow('prev');
      this.nextBtn = this.makeArrow('next');
      nav.appendChild(this.prevBtn);

      if (mode === 'dots') {
        var dots = document.createElement('div');
        dots.className = 'lc-slider-nav__dots';
        this.dots = this.slides.map(function (slide, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'lc-slider-dot';
          b.setAttribute('aria-label', String(i + 1));
          b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
          b.addEventListener(
            'click',
            function () {
              this.goTo(i);
            }.bind(this)
          );
          dots.appendChild(b);
          return b;
        }, this);
        nav.appendChild(dots);
      }

      /* index は dots モードでも必ず作る（≤480 で CSS が dots と入れ替える）。 */
      this.indexEl = document.createElement('p');
      this.indexEl.className = 'lc-slider-index';
      this.indexEl.setAttribute('aria-live', 'polite');
      nav.appendChild(this.indexEl);

      nav.appendChild(this.nextBtn);
      this.appendChild(nav);
      this.nav = nav;
      this.syncNav();
    }

    makeArrow(dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'lc-slider-arrow lc-slider-arrow--' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? '前へ' : '次へ');
      b.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">' +
        '<path d="' +
        (dir === 'prev' ? 'M15 4 7 12l8 8' : 'M9 4l8 8-8 8') +
        '" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
      b.addEventListener(
        'click',
        function () {
          this.goTo(this.index + (dir === 'prev' ? -1 : 1));
        }.bind(this)
      );
      return b;
    }

    /* --- 現在位置の追跡 -------------------------------------------------------
     * threshold [0, .5]: 0 は「画面内にいるか」＝ inert 判定、
     * .5 は「主役として見えているか」＝ current index 判定に使う。 */
    observe() {
      if (!('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var slide = entry.target;
            var visible = entry.intersectionRatio > 0;
            /* 画面外のスライドだけ inert（読み上げ・タブ順から外す）。
               複数枚同時表示のスライダーでも current 以外を殺さない。 */
            if (this.slides.length > 1) slide.toggleAttribute('inert', !visible);
            if (entry.intersectionRatio >= 0.5 && Date.now() >= this.lockUntil) {
              var i = this.slides.indexOf(slide);
              if (i > -1 && i !== this.index) {
                this.index = i;
                this.syncNav();
                this.emit();
              }
            }
          }, this);
        }.bind(this),
        { root: this.track, threshold: [0, 0.5] }
      );
      this.slides.forEach(function (s) {
        this.io.observe(s);
      }, this);
    }

    goTo(i) {
      var max = this.slides.length - 1;
      var next = Math.min(Math.max(i, 0), max);
      var slide = this.slides[next];
      if (!slide) return;
      var left =
        this.track.scrollLeft +
        (slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left);
      this.lockUntil = Date.now() + 500;
      this.index = next;
      this.syncNav();
      this.emit();
      this.track.scrollTo({ left: left, behavior: reduced.matches ? 'auto' : 'smooth' });
    }

    syncNav() {
      var total = this.slides.length;
      if (this.dots) {
        this.dots.forEach(function (d, i) {
          d.setAttribute('aria-current', i === this.index ? 'true' : 'false');
        }, this);
      }
      if (this.indexEl) this.indexEl.textContent = this.index + 1 + ' / ' + total;
      if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index >= total - 1;
    }

    emit() {
      this.dispatchEvent(
        new CustomEvent('change', {
          bubbles: true,
          detail: { index: this.index, total: this.slides.length },
        })
      );
    }

    handleKeydown(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      /* 入力欄の中では矢印はキャレット移動が正しい挙動 */
      var t = e.target;
      if (t && t.closest && t.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      this.goTo(this.index + (e.key === 'ArrowLeft' ? -1 : 1));
    }

    /* --- オートプレイ（ホームでは data-autoplay を書かない＝常に無効） -------- */
    startAutoplay() {
      if (!this.autoplayMs || reduced.matches || this.slides.length < 2) return;
      var tick = function () {
        if (document.hidden) return;
        var next = this.index >= this.slides.length - 1 ? 0 : this.index + 1;
        this.goTo(next);
      }.bind(this);
      this.timer = setInterval(tick, this.autoplayMs);
      /* 操作中・注視中は止める（WCAG 2.2.2 の一時停止手段も兼ねる） */
      var pause = this.stopAutoplay.bind(this);
      this.addEventListener('pointerenter', pause);
      this.addEventListener('focusin', pause);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }
  }

  define('lc-slider', LcSlider);

  /* ===========================================================================
   * §2. <lc-marquee>
   * ---------------------------------------------------------------------------
   * 実測: rAF が毎フレーム translate3d を直書きし、時間ベース（位置 += 速度×経過秒）
   * でフレームレート非依存に流す。1440px で 199.6px/s・375px で 133.2px/s
   * （= 設定 200px/s を innerWidth<=768 で ÷1.5）。複製は ceil(box/W)+1 セット。
   * 参照テーマは reduced-motion 非対応。当方は rAF を回さず「その位置で静止」。
   * ======================================================================== */
  class LcMarquee extends HTMLElement {
    connectedCallback() {
      if (this.dataset.lcReady === 'true') return;
      this.track = this.querySelector('.lc-marquee__track');
      if (!this.track) return;
      /* 走行は transform 駆動なのでスクロール領域ではなくなる。no-JS フォールバックの
         overflow-x:auto と tabindex（マークアップ側）は JS 起動時に引っ込める。
         reduced-motion では走行しない＝スクロール到達を残す。 */
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.style.overflowX = 'hidden';
        this.removeAttribute('tabindex');
        this.removeAttribute('role');
        this.removeAttribute('aria-label');
      }
      this.originals = Array.prototype.slice.call(this.track.children);
      if (!this.originals.length) return;
      this.dataset.lcReady = 'true';

      this.baseSpeed = parseFloat(this.dataset.speed) || 200;
      this.dir = this.dataset.direction === 'right' ? 1 : -1;
      this.offset = 0;
      this.setW = 0;
      this.visible = true;
      this.raf = null;

      this.fill();

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.fill.bind(this));
        this.ro.observe(this);
      }
      if ('IntersectionObserver' in window) {
        this.vo = new IntersectionObserver(
          function (entries) {
            this.visible = entries[0].isIntersecting;
            this.visible ? this.start() : this.stop();
          }.bind(this)
        );
        this.vo.observe(this);
      }
      this.onMotionChange = function () {
        reduced.matches ? this.stop() : this.start();
      }.bind(this);
      reduced.addEventListener('change', this.onMotionChange);

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.ro) this.ro.disconnect();
      if (this.vo) this.vo.disconnect();
      reduced.removeEventListener('change', this.onMotionChange);
    }

    /** 1セット分の幅を測り、コンテナを覆う数＋1セットぶん複製する。 */
    fill() {
      Array.prototype.slice
        .call(this.track.querySelectorAll('[data-lc-clone="true"]'))
        .forEach(function (n) {
          n.remove();
        });

      var w = 0;
      this.originals.forEach(function (n) {
        w += n.getBoundingClientRect().width;
      });
      this.setW = w;
      if (!w) return;

      var box = this.getBoundingClientRect().width;
      var sets = Math.ceil(box / w) + 1;
      for (var s = 1; s < sets; s++) {
        this.originals.forEach(function (n) {
          var c = n.cloneNode(true);
          c.dataset.lcClone = 'true';
          c.setAttribute('aria-hidden', 'true');
          this.track.appendChild(c);
        }, this);
      }
      this.render();
    }

    speed() {
      /* 実測どおり 768px 以下で 1/1.5 に落とす（見かけの速度を揃えるため） */
      return window.innerWidth <= 768 ? this.baseSpeed / 1.5 : this.baseSpeed;
    }

    start() {
      if (this.raf || reduced.matches || !this.visible || !this.setW) return;
      this.last = 0;
      this.raf = requestAnimationFrame(this.step.bind(this));
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    step(now) {
      if (!this.last) this.last = now;
      var dt = now - this.last;
      this.last = now;
      this.offset = (this.offset + (this.speed() * dt) / 1000) % this.setW;
      this.render();
      this.raf = requestAnimationFrame(this.step.bind(this));
    }

    render() {
      /* 右流しは -1セット分から 0 へ向かって動かす（左端に隙間を作らない） */
      var x = this.dir === 1 ? this.offset - this.setW : -this.offset;
      this.track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }
  }

  define('lc-marquee', LcMarquee);

  /* ===========================================================================
   * §3. <lc-hotspots>
   * ---------------------------------------------------------------------------
   * `.lc-hotspot` の中の button でツールチップを開閉する。開いたものを最前面に
   * 出すため z-index はカウンタで単調増加させる（実測どおり・再計算不要）。
   * ======================================================================== */
  var hotspotZ = 10;

  class LcHotspots extends HTMLElement {
    connectedCallback() {
      if (this.dataset.lcReady === 'true') return;
      this.dataset.lcReady = 'true';

      this.onClick = function (e) {
        if (!e.target || typeof e.target.closest !== 'function') return;
        var btn = e.target.closest('.lc-hotspot button');
        if (!btn || !this.contains(btn)) return;
        var spot = btn.closest('.lc-hotspot');
        var open = spot.classList.contains('is-open');
        this.closeAll();
        if (!open) {
          spot.classList.add('is-open');
          spot.style.zIndex = String(++hotspotZ);
          btn.setAttribute('aria-expanded', 'true');
          this.lastTrigger = btn;
        }
      }.bind(this);

      this.onDocClick = function (e) {
        if (!this.contains(e.target)) this.closeAll();
      }.bind(this);

      this.onKeydown = function (e) {
        if (e.key !== 'Escape') return;
        if (!this.querySelector('.lc-hotspot.is-open')) return;
        var back = this.lastTrigger;
        this.closeAll();
        if (back) back.focus();
      }.bind(this);

      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeydown);

      /* aria の初期同期。JS なしでも中身は読めるので初期状態は「閉」でよい。 */
      Array.prototype.slice.call(this.querySelectorAll('.lc-hotspot button')).forEach(function (b) {
        if (!b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', 'false');
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    closeAll() {
      Array.prototype.slice.call(this.querySelectorAll('.lc-hotspot')).forEach(function (spot) {
        spot.classList.remove('is-open');
        var b = spot.querySelector('button');
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    }
  }

  define('lc-hotspots', LcHotspots);

  /* ===========================================================================
   * §4. <lc-drawer> ＋ グローバル .lc-overlay
   * ---------------------------------------------------------------------------
   * トリガは `data-lc-drawer-open="#cart-drawer"`（委譲クリック＝後から差し込まれた
   * DOM にも効く）。閉じるのは `[data-lc-drawer-close]` / Escape / overlay クリック。
   * 開いている間は drawer 以外の body 直下要素を inert にする（背面のタブ移動と
   * 読み上げを止める＝フォーカストラップをイベントで自作するより堅い）。
   *
   * ★ `.lc-drawer` クラスは**ここが付ける**（セクションのマークアップに直書き
   *   しない）。直書きすると JS が落ちた環境で visibility:hidden のまま残り、
   *   中身に到達できなくなる＝規約9（JS なしで全内容が読める）に反する。
   * ======================================================================== */
  var drawerState = { open: null, trigger: null, inerted: [], pad: '' };

  function overlayEl() {
    return document.querySelector('.lc-overlay');
  }

  function lockScroll(on) {
    var body = document.body;
    if (on) {
      /* スクロールバーが消える分だけ右に padding を足して横ズレ（CLS）を防ぐ。
         local プリセットは scrollbar-gutter を敷いていないのでここで補う。 */
      var gap = window.innerWidth - document.documentElement.clientWidth;
      drawerState.pad = body.style.paddingInlineEnd;
      if (gap > 0) body.style.paddingInlineEnd = gap + 'px';
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
      body.style.paddingInlineEnd = drawerState.pad;
    }
  }

  function setBackgroundInert(drawer, on) {
    if (on) {
      drawerState.inerted = [];
      Array.prototype.slice.call(document.body.children).forEach(function (el) {
        if (el === drawer || el.contains(drawer) || el.classList.contains('lc-overlay')) return;
        if (el.hasAttribute('inert')) return;
        el.setAttribute('inert', '');
        drawerState.inerted.push(el);
      });
    } else {
      drawerState.inerted.forEach(function (el) {
        el.removeAttribute('inert');
      });
      drawerState.inerted = [];
    }
  }

  function openDrawer(drawer, trigger) {
    if (!drawer || drawerState.open === drawer) return;
    if (drawerState.open) closeDrawer();
    drawer.classList.add('is-open');
    drawer.removeAttribute('inert');
    var ov = overlayEl();
    if (ov) ov.classList.add('is-active');
    setBackgroundInert(drawer, true);
    lockScroll(true);
    drawerState.open = drawer;
    drawerState.trigger = trigger || null;

    var first = drawer.querySelector(FOCUSABLE);
    if (first) {
      first.focus();
    } else {
      if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
      drawer.focus();
    }
  }

  function closeDrawer() {
    var drawer = drawerState.open;
    if (!drawer) return;
    drawer.classList.remove('is-open');
    var ov = overlayEl();
    if (ov) ov.classList.remove('is-active');
    setBackgroundInert(drawer, false);
    lockScroll(false);
    drawerState.open = null;
    var back = drawerState.trigger;
    drawerState.trigger = null;
    if (back && document.contains(back)) back.focus();
  }

  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;
    var opener = e.target.closest('[data-lc-drawer-open]');
    if (opener) {
      var sel = opener.getAttribute('data-lc-drawer-open');
      var target = sel && document.querySelector(sel);
      if (target) {
        e.preventDefault();
        openDrawer(target, opener);
      }
      return;
    }
    if (e.target.closest('[data-lc-drawer-close]') || e.target.classList.contains('lc-overlay')) {
      closeDrawer();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawerState.open) closeDrawer();
  });

  class LcDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.lcReady === 'true') return;
      this.dataset.lcReady = 'true';
      var side = this.dataset.side === 'left' ? 'left' : 'right';
      this.classList.add('lc-drawer', 'lc-drawer--' + side);
      if (!this.hasAttribute('role')) this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
    }

    open(trigger) {
      openDrawer(this, trigger);
    }

    close() {
      if (drawerState.open === this) closeDrawer();
    }
  }

  define('lc-drawer', LcDrawer);

  /* ===========================================================================
   * §5. LcStickyHeader
   * ---------------------------------------------------------------------------
   * 実測: 上スクロール中かつ基準要素 rect.top < -100px で `.show`、下方向で解除、
   * デッドゾーン 5px、scrollY<=0 で解除。参照テーマはヘッダーを body 直下へ
   * クローンしていたが、当方は「同一 DOM 内の fixed バー」を制御する
   * （ID 衝突なし・読み上げの重複なし＝a11y 有利）。DOM は local-header 側が用意する。
   *
   *   window.LcStickyHeader.init(headerEl, barEl)
   * ======================================================================== */
  var stickyBound = new WeakSet();

  window.LcStickyHeader = {
    init: function (headerEl, barEl) {
      if (!headerEl || !barEl || stickyBound.has(barEl)) return;
      stickyBound.add(barEl);

      var last = window.scrollY;

      window.addEventListener(
        'scroll',
        function () {
          var y = window.scrollY;
          var dy = y - last;
          /* デッドゾーン: 慣性スクロールの微振動で show/hide が点滅するのを防ぐ */
          if (Math.abs(dy) < 5) return;
          last = y;

          if (y <= 0) {
            barEl.classList.remove('show');
            return;
          }
          if (dy < 0 && headerEl.getBoundingClientRect().top < -100) {
            barEl.classList.add('show');
          } else if (dy > 0) {
            barEl.classList.remove('show');
          }
        },
        { passive: true }
      );
    },
  };
})();
